import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../../utils/api';
import { Bell, RefreshCw, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { isPrintFailAlertMuted, setPrintFailAlertMuted } from '../../utils/soundAlert';
import { toast } from 'sonner';

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

const orderNumber = (o) =>
  o.daily_order_number
    ? `#${String(o.daily_order_number).padStart(3, '0')}`
    : `#${(o.id || '').slice(-6).toUpperCase()}`;

const relTime = (ts) => {
  const d = new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return d.toLocaleDateString();
};

/**
 * POS Alerts — feed of recent orders + sound-mute control.
 *
 * Reuses /api/admin/orders/new which is now allowed for POS-scoped tokens.
 * Marking "read" is a client-side concept (localStorage) shared with the
 * NotificationCenter — but this feed doesn't need it, since the POS
 * cashier taps into Orders to actually action tickets.
 */
export default function PosAlertsPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(isPrintFailAlertMuted());

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await adminAPI.getNewOrdersCount();
      setOrders(res.data?.recent_orders || []);
    } catch (_err) {
      // silent — polling will retry
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 5000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  const toggleMute = () => {
    const next = !muted;
    setPrintFailAlertMuted(next);
    setMuted(next);
    toast.info(next ? 'Print-fail sound muted' : 'Print-fail sound enabled');
  };

  return (
    <div className="p-4" data-testid="pos-alerts-page">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold">Alerts</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleMute}
            className={`p-2 rounded-full ${muted ? 'text-neutral-400' : 'text-neutral-800'} hover:bg-neutral-200`}
            data-testid="pos-alerts-mute"
            aria-label={muted ? 'Unmute print-fail sound' : 'Mute print-fail sound'}
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={fetchAlerts}
            className="p-2 rounded-full hover:bg-neutral-200"
            data-testid="pos-alerts-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-neutral-500" data-testid="pos-alerts-empty">
          <Bell className="h-10 w-10 mx-auto opacity-30 mb-2" />
          No recent alerts.
        </div>
      ) : (
        <div className="space-y-2" data-testid="pos-alerts-list">
          {orders.slice(0, 20).map((o) => (
            <div
              key={o.id}
              className="p-3 rounded-lg bg-white border border-neutral-200"
              data-testid={`pos-alert-${o.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">
                  {orderNumber(o)} · {o.customer_name}
                </div>
                <div className="text-sm font-bold">{gbp(o.total_amount)}</div>
              </div>
              <div className="flex items-center justify-between text-xs text-neutral-500 mt-1">
                <span className="capitalize">
                  {o.order_type} · {(o.items || []).reduce((s, it) => s + (it.quantity || 0), 0)} items
                </span>
                <span>{relTime(o.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
